import { CaretDown, CaretUp } from '@phosphor-icons/react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface SuggestionsHeaderTitleNavProps {
  title: React.ReactNode;
  breadcrumb?: string;
  onOpenArticleLink?: () => void;
  currentArticleIndex: number;
  totalArticles: number;
  displayTotalArticles?: number;
  onNextArticle: () => void;
  onPreviousArticle: () => void;
}

export function SuggestionsHeaderTitleNav({
  title,
  breadcrumb,
  onOpenArticleLink,
  currentArticleIndex,
  totalArticles,
  displayTotalArticles,
  onNextArticle,
  onPreviousArticle,
}: SuggestionsHeaderTitleNavProps) {
  const hasNextArticle = totalArticles > 0 && currentArticleIndex < totalArticles - 1;
  const hasPreviousArticle = currentArticleIndex > 0;
  const currentArticle = totalArticles === 0 ? 0 : currentArticleIndex + 1;
  const totalArticlesForDisplay = displayTotalArticles ?? totalArticles;
  const hasDisplayArticles = totalArticlesForDisplay > 0;

  return (
    <div className="flex justify-between items-center">
      <h1 className="heading-lg">
        {title}
        {onOpenArticleLink && breadcrumb && (
          <Button
            variant="link"
            size="sm"
            onClick={onOpenArticleLink}
            aria-label="Open article"
            className="block px-0 text-subtle text-xs"
          >
            {breadcrumb}
          </Button>
        )}
      </h1>
      <div className="flex items-center">
        <p className="text-xs text-foreground pr-2">
          <span aria-hidden="true">{hasDisplayArticles ? `${currentArticle}/${totalArticlesForDisplay}` : '0'}</span>
          <span className="sr-only" aria-live="polite">
            {hasDisplayArticles ? `Article ${currentArticle} of ${totalArticlesForDisplay}` : 'No articles'}
          </span>
        </p>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={!hasNextArticle}
              onClick={onNextArticle}
              aria-label="Go to next article"
            >
              <CaretDown aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" bgColor="--bfc-foreground" className="text-background">
            Next Article
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={!hasPreviousArticle}
              onClick={onPreviousArticle}
              aria-label="Go to previous article"
            >
              <CaretUp aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" bgColor="--bfc-foreground" className="text-background">
            Previous Article
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
